import { ReactNode } from 'react';
import { format } from 'date-fns';
import { useReduxState } from 'hooks';
import {
  Alert,
  Button,
  ConfirmButton,
  ConfirmDeleteButton,
  MoreOptions
} from 'components/common';
import { Icon } from 'components/common/icons';

interface Props {
  children?: ReactNode;
  description?: string;
  expirationDate?: string;
  firstGrantDate?: string;
  grantors?: ReactNode;
  id: number;
  isChecked?: boolean;
  isCheckedPending?: boolean;
  name: string;
  onDelete?: (id: number, deletionReason?: string) => Promise<boolean>;
  onDeleteConfirmWarning?: string;
  onRemove?: (id: number) => Promise<void> | void;
}

const CompetenciesCard = ({
  children,
  expirationDate,
  description,
  firstGrantDate,
  grantors,
  id,
  isChecked,
  isCheckedPending,
  name,
  onDelete,
  onDeleteConfirmWarning,
  onRemove
}: Props) => {
  const {
    messages,
    theme,
    serverConfig: { requireReasonWhenDeletingData }
  } = useReduxState();

  return (
    <div className="competency">
      <div className="competency-content">
        <div className="header">
          {isChecked || isCheckedPending ? (
            <div
              className={`status ${isCheckedPending ? 'status-pending' : ''}`}
            >
              <Icon icon="check" />
            </div>
          ) : null}

          <div className="content">
            <div className="title">{name}</div>

            {description ? (
              <div className="description">{description}</div>
            ) : null}

            {grantors ? (
              <>
                {firstGrantDate ? (
                  <div className="item">
                    <div className="item-label">
                      {messages.commonGrantedOn}{' '}
                    </div>
                    <div className="grant-date">
                      <time>
                        {format(Date.parse(firstGrantDate), 'dd.MM.y')}
                      </time>
                    </div>
                  </div>
                ) : null}
                <div className="item">
                  <div className="item-label">{messages.commonGrantedBy}</div>
                  <div className="grantors">{grantors}</div>
                </div>
              </>
            ) : null}

            {expirationDate ? (
              <div className="item">
                <div className="item-label">{messages.commonExpires}</div>

                <div className="expiration-date">
                  {format(Date.parse(expirationDate as string), 'dd.MM.y')}
                </div>
              </div>
            ) : null}
          </div>

          {onDelete ? (
            <MoreOptions renderAsFixed={true} styleContentButtons={false}>
              {requireReasonWhenDeletingData ? (
                <ConfirmDeleteButton
                  buttonIsDropdown={true}
                  buttonText={messages.competenciesRemove}
                  confirmText={`${
                    onDeleteConfirmWarning ? `${onDeleteConfirmWarning} ` : ''
                  }${messages.competenciesRemoveConfirm}`}
                  confirmTitle={messages.competenciesRemove}
                  id={`deletionReasonUserCompetency-${id}`}
                  onConfirm={(values) => onDelete(id, values?.deletionReason)}
                  successMessage={messages.competenciesRemoveSuccess}
                />
              ) : (
                <ConfirmButton
                  actionText={messages.commonRemove}
                  buttonIsDropdown={true}
                  buttonText={messages.competenciesRemove}
                  category="danger"
                  confirmText={
                    <>
                      {onDeleteConfirmWarning ? (
                        <Alert
                          type="warning-light"
                          style={{ marginBottom: 15 }}
                          text={onDeleteConfirmWarning}
                        />
                      ) : null}

                      {messages.competenciesRemoveConfirm}
                    </>
                  }
                  confirmTitle={messages.competenciesRemove}
                  onConfirm={() => onDelete(id)}
                  successMessage={messages.competenciesRemoveSuccess}
                />
              )}
            </MoreOptions>
          ) : onRemove ? (
            <Button
              category="lighter"
              icon="trash-alt"
              onClick={() => onRemove(id)}
              size="header"
              style={{ flexShrink: 0, margin: '-3px 0' }}
              tooltip={messages.commonRemove}
            />
          ) : null}
        </div>
      </div>

      {children}

      <style jsx>{`
        .competency {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
        }
        .competency-content {
          padding: 15px;
        }
        .header {
          display: flex;
          gap: 15px;
        }
        .content {
          flex-grow: 1;
        }
        .title {
          font-size: 1.6rem;
          font-weight: 700;
          padding: 2px 0;
        }
        .description {
          font-size: 1.5rem;
          font-weight: 400;
          line-height: 1.4;
          margin-top: 5px;
        }
        .status {
          align-items: center;
          background-color: ${theme.successColor};
          border-radius: 20px;
          color: ${theme.successTextColor};
          display: flex;
          flex-shrink: 0;
          font-size: 1rem;
          justify-content: center;
          margin-top: 3px;
          height: 20px;
          width: 20px;
        }
        .status-pending {
          background-color: ${theme.lightGreyDark};
          color: ${theme.textColorLight};
        }
        .item {
          display: flex;
          gap: 15px;
          margin-top: 10px;
        }
        .item-label {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 3px;
          white-space: nowrap;
        }
        .grantors {
          display: flex;
          flex-wrap: wrap;
          gap: 10px 20px;
        }
        .expiration-date {
          margin-top: 3px;
        }
        .grant-date {
          margin-top: 3px;
          font-size: 1.4 rem;
        }
      `}</style>
    </div>
  );
};

export default CompetenciesCard;
